'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import CCPALink from '@/components/CCPALink';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const currentYear = new Date().getFullYear();

  const navigationLinks = [
    { label: 'Home', href: '/homepage' },
    { label: 'Services', href: '/services' },
    { label: 'Why Danny Clans', href: '/why-danny-clans' },
    { label: 'Contact', href: '/contact' },
  ];

  const complianceLinks = [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Cookie Policy', href: '/cookies' },
    { label: 'Terms of Service', href: '/terms-of-service' },
    { label: 'Refund Policy', href: '/refund-policy' },
    { label: 'KYC & AML Policy', href: '/kyc-aml-policy' },
  ];

  const resourceLinks = [
    { label: 'Technical Documentation', href: '/technical-docs' },
    { label: 'Compliance Guides', href: '/compliance-guides' },
    { label: 'Regulatory Updates', href: '/regulatory-updates' },
    { label: 'Platform Integrations', href: '/platform-integrations' },
  ];

  const handleCookiePreferences = () => {
    // This will be handled by the CookieBanner component
    // For now, we'll dispatch a custom event that the banner can listen to
    window.dispatchEvent(new CustomEvent('openCookiePreferences'));
  };

  return (
    <footer className={`bg-surface border-t border-border ${className}`}>
      <div className="mx-auto px-6 lg:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <Link href="/homepage" className="flex items-center mb-6">
              <div className="flex items-center space-x-2">
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-primary"
                >
                  <rect width="40" height="40" rx="4" fill="currentColor" />
                  <path
                    d="M12 12h6c4.418 0 8 3.582 8 8s-3.582 8-8 8h-6V12z"
                    fill="white"
                  />
                  <circle cx="18" cy="20" r="3" fill="currentColor" />
                </svg>
                <span className="font-headline font-semibold text-lg text-primary">
                  Danny Clans Media
                </span>
              </div>
            </Link>
            <p className="font-body text-sm text-text-secondary mb-4 leading-relaxed">
              Infrastructure-grade advertising solutions. Hong Kong incorporated for global regulatory compliance.
            </p>
            <div className="flex items-center space-x-2 text-success">
              <Icon name="CheckCircleIcon" size={20} variant="solid" />
              <span className="font-accent text-xs text-text-secondary">
                Regulatory Compliant
              </span>
            </div>
          </div>

          <div>
            <h3 className="font-headline font-semibold text-sm text-primary mb-4">
              Navigation
            </h3>
            <ul className="space-y-3">
              {navigationLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="font-body text-sm text-text-secondary hover:text-accent transition-colors duration-smooth"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-headline font-semibold text-sm text-primary mb-4">
              Compliance
            </h3>
            <ul className="space-y-3">
              {complianceLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="font-body text-sm text-text-secondary hover:text-accent transition-colors duration-smooth"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-headline font-semibold text-sm text-primary mb-4">
              Resources
            </h3>
            <ul className="space-y-3">
              {resourceLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="font-body text-sm text-text-secondary hover:text-accent transition-colors duration-smooth"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-6">
              <p className="font-body text-sm text-text-secondary">
                © {currentYear} Danny Clans Media. All rights reserved.
              </p>
              <span className="font-accent text-xs text-text-secondary">
                Hong Kong SAR
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4">
              <button
                onClick={handleCookiePreferences}
                className="px-5 py-2 font-body text-sm font-semibold text-text-secondary border border-border rounded-sm hover:bg-surface-hover transition-colors duration-smooth"
              >
                Cookie Preferences
              </button>
              <CCPALink />
              <Link
                href="/contact"
                className="px-5 py-2 bg-primary text-primary-foreground font-headline font-semibold text-sm rounded-sm hover:bg-secondary transition-colors duration-smooth"
              >
                Technical Consultation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;